import { useRegisterStore } from '../store/register'
import { useLoginStore } from '../store/login'
import { _i18n } from '@/micro/languages'

/**
 * Modifica los mensajes de error que llegan de la API al momento del registro.
 *
 * @param {String} message Mensaje de error por defecto.
 * @param {Number} code Código del error.
 *
 * @returns {String} Mensaje de error modificado o null.
 */
export const modifyRegisterErrorMessage = (message: string, code: number) => {
  const state = useRegisterStore()

  const emailCreatedMessage =
    'Hubo un error al crear tu cuenta, revisa que tu correo sea correcto e inténtalo de nuevo.'

  const analyze6004 = (): string => {
    const email = state.email
    const domain = email?.split('@').pop()

    if (domain && message.includes('No se permiten nombres de red local')) {
      return `
          '${domain}' no es un nombre de dominio válido para el correo '${email}' <br>
          '${domain}' parece ser un dominio pero no coincide con las listas TLD conocidas <br>
          '${domain}' parece ser un nombre de red local. No se permiten nombres de red local <br>
        `
    }

    if (message.includes('Ocurrió un error en el atributo : "password"')) {
      return message.replace('Ocurrió un error en el atributo : "password".', '').trim()
    }

    return emailCreatedMessage
  }

  const errorHandlers: Record<number, () => string> = {
    6004: analyze6004,
    6066: () => message,
    6067: () => `El cupón ${state.coupon} no es válido`,
    9: () => emailCreatedMessage,
    6144: () => emailCreatedMessage,
  }

  return errorHandlers[code]?.() || null
}

/**
 * Modifica los mensajes de error que llegan de la API al momento del login.
 *
 * @param {String} message Mensaje de error por defecto.
 * @param {Number} code Código del error.
 *
 * @returns {String} Mensaje de error modificado o null.
 */
export const modifyLoginErrorMessage = (message: string, code: number | string) => {
  const state = useLoginStore()

  const analyze400 = (): string => {
    switch (message) {
      case 'unauthorized':
      case 'user not found':
        return 'Usuario o clave inválida. ¡Inténtalo de nuevo!'
      case 'Locked':
        return 'Tu cuenta ha sido bloqueada por intentos fallidos, hemos <br/>enviado un correo con las instrucciones para restablecer tu contraseña'
      case 'google login is not enabled':
        return 'Acceso con Google no habilitado. Ingresa con tu contraseña y actívalo desde Seguridad.'
      default:
        return message
    }
  }

  const errorHandlersStr: Record<string, () => string> = {
    INVALID_OTP: () => _i18n('validateIdentity.otp.errorCode', { attempts: getRemainingAttemptsMessage(state.userFailures) }),
  }

  const errorHandlers: Record<number, () => string> = {
    22017: () => message,
    400: analyze400,
  }

  if (typeof code === 'string') {
    return errorHandlersStr[code]?.() || message
  }

  return errorHandlers[code]?.() || message
}

/**
 * Genera un mensaje de intentos restantes con pluralización correcta.
 *
 * @param {Number} failedAttempts Número de intentos fallidos del usuario.
 * @param {Number} maxAttempts Número máximo de intentos permitidos (por defecto: 3).
 *
 * @returns {String} Mensaje formateado con intentos restantes.
 *
 * @example
 * getRemainingAttemptsMessage(1) // 'queda 1 intento'
 * getRemainingAttemptsMessage(2, 3) // 'quedan 1 intento'
 * getRemainingAttemptsMessage(1, 5) // 'quedan 4 intentos'
 */
export const getRemainingAttemptsMessage = (
  failedAttempts: number,
  maxAttempts: number = 3
): string => {
  const remaining = maxAttempts - failedAttempts
  const prefix = remaining === 1 ? 'queda' : 'quedan'
  const word = remaining === 1 ? 'intento' : 'intentos'
  return `${prefix} ${remaining} ${word}`
}

